import React, { useCallback, useEffect, useState } from 'react';
import { Landmark, Download, Trash2, Pencil, Info, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatINR, amountInWords, portalDate, todayLocal } from '../utils/indianMoney';

const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });
const CRORE = 10000000;

// Hands the browser a file it already holds in memory and asks it to save it
const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// The Excel file uploaded on the Indian Oil portal to pay for fuel: Indent Date, Indent No, Amount, Dealer Code.
// Type the date and the amount - the number follows on from the last one - and download the file.
const IoclIndent = () => {
  const [data, setData] = useState(null); // { dealer_code, next_serial, first_use, last_known_serial, items }
  const [date, setDate] = useState(todayLocal());
  const [amount, setAmount] = useState('');
  const [serialText, setSerialText] = useState(''); // '' = let the number follow on by itself
  const [changingNo, setChangingNo] = useState(false);
  const [busy, setBusy] = useState(false);
  const isAdmin = localStorage.getItem('role') === 'admin';

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/iocl-indents', { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Could not load the indent list');
      setData(json);
    } catch (err) {
      toast.error(err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadFile = async (item) => {
    const res = await fetch(`/api/iocl-indents/${item.id}/file`, { headers: authHeaders() });
    if (!res.ok) {
      const json = await res.json().catch(() => ({}));
      throw new Error(json.error || 'Could not download the file');
    }
    saveBlob(await res.blob(), item.file_name);
  };

  const dealer = data?.dealer_code || '';
  const amountNumber = Number(amount);
  const serialNumber = serialText === '' ? data?.next_serial : Number(serialText);
  const serialOk = Number.isInteger(serialNumber) && serialNumber >= 1 && serialNumber <= 999999;
  const amountOk = Number.isFinite(amountNumber) && amountNumber > 0;
  const canMake = !!data && !!dealer && amountOk && serialOk && /^\d{4}-\d{2}-\d{2}$/.test(date) && !busy;
  const indentNo = data && dealer && serialOk ? `${dealer}/${serialNumber}` : '';

  const handleMake = async () => {
    if (!canMake) return;
    // A payment is about to be made from this file, so it gets two chances to catch a slip
    const twin = data.items.find(i => i.indent_date === date && Number(i.amount) === amountNumber);
    if (twin && !window.confirm(`An indent for ${formatINR(amountNumber)} dated ${portalDate(date)} was already made (${twin.indent_no}).\n\nMake another one?`)) return;
    if (amountNumber >= CRORE && !window.confirm(`${formatINR(amountNumber)}\n${amountInWords(amountNumber)}\n\nIs this amount correct?`)) return;

    setBusy(true);
    try {
      const res = await fetch('/api/iocl-indents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ indent_date: date, amount: amountNumber, serial: serialText === '' ? undefined : serialNumber })
      });
      const json = await res.json();
      if (!res.ok) {
        toast.error(json.error || 'Could not make the indent');
        await load();
        return;
      }
      // The indent now exists whatever happens next, so reset the form either way
      setAmount('');
      setSerialText('');
      setChangingNo(false);
      try {
        await downloadFile(json.item);
        toast.success(`Indent ${json.item.indent_no} made - the file is downloading`);
      } catch (err) {
        toast.error(`Indent ${json.item.indent_no} was saved, but the download failed (${err.message}). Use Download in the list below.`);
      }
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async (item) => {
    try {
      await downloadFile(item);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete indent ${item.indent_no} (${formatINR(item.amount)})?\n\nOnly do this if it was never uploaded on the portal - its number will be used again.`)) return;
    try {
      const res = await fetch(`/api/iocl-indents/${item.id}`, { method: 'DELETE', headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Could not delete');
      toast.success(`Indent ${item.indent_no} deleted`);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const newestId = data?.items?.[0]?.id;

  return (
    <div className="max-w-4xl mx-auto pb-16 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Landmark className="w-6 h-6 text-indigo-600" /> IOCL Indent File
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Type the date and the amount, download the Excel file and upload it on the Indian Oil portal. The indent number follows on by itself.
        </p>
      </div>

      {data && !dealer && (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <span>The Dealer Code is not set. Add it in <b>Settings</b> first - it is written into every file.</span>
        </div>
      )}

      {data?.first_use && dealer && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl p-4 text-sm">
          <Info className="w-5 h-5 shrink-0" />
          <span>
            Nothing has been made here yet. Your Excel file ended at <b>{dealer}/{data.last_known_serial}</b>, so this starts at{' '}
            <b>{dealer}/{data.next_serial}</b>. If that is not the right next number, click <b>Change</b> next to the number.
          </span>
        </div>
      )}

      <div className="glass-panel p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1" htmlFor="indent-date">Indent date</label>
            <input id="indent-date" type="date" className="input-field" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1" htmlFor="indent-amount">Amount (₹)</label>
            <input
              id="indent-amount" type="number" min="1" step="0.01" inputMode="decimal" className="input-field" placeholder="e.g. 1000000"
              value={amount} onChange={e => setAmount(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleMake(); }}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1" htmlFor="indent-no">Indent no.</label>
            {changingNo ? (
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm text-slate-600">{dealer}/</span>
                <input
                  id="indent-no" type="number" min="1" step="1" className="input-field" autoFocus
                  value={serialText} placeholder={String(data?.next_serial ?? '')} onChange={e => setSerialText(e.target.value)}
                />
                <button type="button" className="text-xs text-indigo-600 hover:underline shrink-0" onClick={() => { setChangingNo(false); setSerialText(''); }}>Auto</button>
              </div>
            ) : (
              <div className="flex items-center gap-2 h-[42px]">
                <span id="indent-no" className="font-mono font-semibold text-slate-900">{indentNo || '-'}</span>
                <button type="button" className="text-xs text-indigo-600 hover:underline flex items-center gap-1" onClick={() => setChangingNo(true)}>
                  <Pencil className="w-3 h-3" /> Change
                </button>
              </div>
            )}
          </div>
        </div>

        {/* What will be in the file - laid out like the Excel sheet itself */}
        <div>
          <p className="text-xs font-medium text-slate-500 mb-1">This is what the file will contain</p>
          <table className="w-full text-sm border-collapse border border-slate-300">
            <thead>
              <tr className="bg-slate-50">
                <th className="border border-slate-300 p-2 text-right">Indent Date</th>
                <th className="border border-slate-300 p-2 text-left">Indent No</th>
                <th className="border border-slate-300 p-2 text-right">Amount</th>
                <th className="border border-slate-300 p-2 text-left">Dealer Code</th>
              </tr>
            </thead>
            <tbody>
              <tr className="font-mono">
                <td className="border border-slate-300 p-2 text-right">{portalDate(date) || '-'}</td>
                <td className="border border-slate-300 p-2">{indentNo || '-'}</td>
                <td className="border border-slate-300 p-2 text-right">{amountOk ? amountNumber : '-'}</td>
                <td className="border border-slate-300 p-2">{dealer || '-'}</td>
              </tr>
            </tbody>
          </table>
          {amountOk && (
            <p className="mt-2 text-sm text-slate-700">
              <b>{formatINR(amountNumber)}</b> <span className="text-slate-500">- {amountInWords(amountNumber)}</span>
            </p>
          )}
        </div>

        <button
          onClick={handleMake} disabled={!canMake}
          className="btn-primary flex items-center gap-2 px-5 py-2.5 disabled:opacity-50"
        >
          <Download className="w-4 h-4" /> {busy ? 'Making the file...' : 'Download Excel file'}
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800">Indents made</h2>
          <p className="text-xs text-slate-500">Every file made here is kept - download any of them again whenever you need.</p>
        </div>
        {!data ? (
          <div className="p-8 text-center text-slate-500 text-sm">Loading...</div>
        ) : data.items.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">No indents made yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-500 text-xs font-semibold">
                <tr>
                  <th className="p-3">Indent No</th>
                  <th className="p-3">Date</th>
                  <th className="p-3 text-right">Amount</th>
                  <th className="p-3">Made by</th>
                  <th className="p-3">Made on</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map(item => (
                  <tr key={item.id} className="hover:bg-slate-50/50">
                    <td className="p-3 font-mono font-semibold">{item.indent_no}</td>
                    <td className="p-3 font-mono">{portalDate(item.indent_date)}</td>
                    <td className="p-3 text-right font-mono">{formatINR(item.amount)}</td>
                    <td className="p-3 text-slate-600">{item.created_by || '-'}</td>
                    <td className="p-3 text-slate-500 whitespace-nowrap">{new Date(item.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button onClick={() => handleDownload(item)} className="text-indigo-600 hover:text-indigo-800 font-semibold inline-flex items-center gap-1">
                        <Download className="w-4 h-4" /> Download
                      </button>
                      {isAdmin && item.id === newestId && (
                        <button onClick={() => handleDelete(item)} title="Delete (only the newest can be deleted)" className="ml-3 text-slate-400 hover:text-red-500 align-middle">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default IoclIndent;
